import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy, LayoutDashboard, Users, Handshake, ShieldCheck, Settings, LogOut, Menu, X, PackageCheck, RotateCcw, KeyRound, Wallet, Tag, ScanLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import Dashboard from "@/pages/admin/Dashboard";
import Participants from "@/pages/admin/Participants";
import KitPickup from "@/pages/admin/KitPickup";
import Results from "@/pages/admin/Results";
import Refunds from "@/pages/admin/Refunds";
import Expenses from "@/pages/admin/Expenses";
import Coupons from "@/pages/admin/Coupons";
import Scanner from "@/pages/admin/Scanner";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import Sponsors from "@/pages/admin/Sponsors";
import UsersAccess from "@/pages/admin/UsersAccess";
import EventConfig from "@/pages/admin/EventConfig";

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [mobileOpen, setMobileOpen] = useState(false);
  const isSuper = user?.role === "SUPER_ADMIN";
  const perms = user?.permissions || [];
  const can = (p) => isSuper || perms.includes(p);

  const nav = [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, show: can("READ") },
    { id: "participants", label: "Inscrições", icon: Users, show: can("READ") },
    { id: "scanner", label: "Leitor QR", icon: ScanLine, show: can("OPERATOR") },
    { id: "kits", label: "Retirada de Kits", icon: PackageCheck, show: can("READ") },
    { id: "results", label: "Resultados", icon: Trophy, show: can("READ") },
    { id: "refunds", label: "Reembolsos", icon: RotateCcw, show: can("READ") },
    { id: "expenses", label: "Despesas", icon: Wallet, show: can("FINANCE") },
    { id: "coupons", label: "Cupons", icon: Tag, show: isSuper },
    { id: "sponsors", label: "Patrocínios", icon: Handshake, show: can("READ") },
    { id: "users", label: "Acessos (RBAC)", icon: ShieldCheck, show: isSuper },
    { id: "config", label: "Configurações", icon: Settings, show: isSuper },
  ].filter((n) => n.show);

  const doLogout = () => { logout(); navigate("/"); };

  const render = () => {
    switch (tab) {
      case "dashboard": return <Dashboard />;
      case "participants": return <Participants />;
      case "kits": return <KitPickup />;
      case "results": return <Results />;
      case "refunds": return <Refunds />;
      case "expenses": return <Expenses />;
      case "coupons": return <Coupons />;
      case "scanner": return <Scanner />;
      case "sponsors": return <Sponsors />;
      case "users": return <UsersAccess />;
      case "config": return <EventConfig />;
      default: return null;
    }
  };

  const SideContent = () => (
    <>
      <div className="flex items-center gap-2 px-2 mb-8">
        <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center p-1"><img src="/logo-cemoc.png" alt="CEMOC RUN" className="w-full h-full object-contain" /></div>
        <div>
          <div className="font-display text-lg font-black uppercase leading-none">CEMOC RUN</div>
          <div className="text-[10px] text-slate-400 uppercase tracking-widest">{isSuper ? "Gestor Geral" : "Assistente"}</div>
        </div>
      </div>
      <nav className="space-y-1 flex-1">
        {nav.map((n) => (
          <button key={n.id} data-testid={`nav-${n.id}`}
            onClick={() => { setTab(n.id); setMobileOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
              tab === n.id ? "bg-brand text-white" : "text-slate-300 hover:bg-white/5"
            }`}>
            <n.icon className="w-4.5 h-4.5" /> {n.label}
          </button>
        ))}
      </nav>
      <div className="border-t border-white/10 pt-3 mt-3">
        <div className="px-3 mb-2 text-xs text-slate-400 truncate">{user?.email || user?.name}</div>
        <ChangePasswordDialog trigger={
          <button data-testid="admin-change-password-btn"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-slate-300 hover:bg-white/5">
            <KeyRound className="w-4 h-4" /> Alterar senha
          </button>} />
        <button data-testid="admin-logout-btn" onClick={doLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-slate-300 hover:bg-white/5">
          <LogOut className="w-4 h-4" /> Sair
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-ink text-white flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 flex-col bg-surface border-r border-white/10 p-4 fixed inset-y-0">
        <SideContent />
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 glass border-b border-white/10 h-14 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center p-1"><img src="/logo-cemoc.png" alt="CEMOC RUN" className="w-full h-full object-contain" /></div>
          <span className="font-display text-lg font-black uppercase">CEMOC RUN</span>
        </div>
        <Button data-testid="mobile-menu-btn" variant="ghost" size="icon" onClick={() => setMobileOpen(true)}>
          <Menu className="w-5 h-5" />
        </Button>
      </div>
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/60" onClick={() => setMobileOpen(false)}>
          <div className="absolute left-0 inset-y-0 w-64 bg-surface border-r border-white/10 p-4 flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-end mb-2"><button onClick={() => setMobileOpen(false)}><X className="w-5 h-5 text-slate-400" /></button></div>
            <SideContent />
          </div>
        </div>
      )}

      <main className="flex-1 lg:ml-64 pt-14 lg:pt-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8">{render()}</div>
      </main>
    </div>
  );
}
